import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Search, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "react-hot-toast";
import {
  CampusDriveApplication,
  getCampusDriveApplications,
} from "../../services/campusDriveApplications";
import { updatePublicJobApplication } from "../../services/jobsapi";

function useDebounce<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "applied", label: "Applied" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "rejected", label: "Rejected" },
];

const ITEMS_PER_PAGE = 10;

const CampusDriveApplications: React.FC = () => {
  const [applications, setApplications] = useState<CampusDriveApplication[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateError, setDateError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<CampusDriveApplication | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [pendingActionApplication, setPendingActionApplication] = useState<CampusDriveApplication | null>(null);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isShortlistDialogOpen, setIsShortlistDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [jobOptionsList, setJobOptionsList] = useState<Array<{ jobId: string; title: string }>>([]);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchApplications = useCallback(async () => {
    if (dateFrom && dateTo) {
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      if (from > to) {
        return [];
      }
    }

    setIsLoading(true);
    setError(null);
    try {
      const query = {
        search: debouncedSearchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        jobId: jobFilter !== "all" ? jobFilter : undefined,
        fromDate: dateFrom || undefined,
        toDate: dateTo || undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };
      const response = await getCampusDriveApplications(query);
      const normalizedData = Array.isArray(response.data) ? response.data : [];
      const normalizedTotalResults =
        typeof response.totalResults === "number" ? response.totalResults : normalizedData.length;
      const normalizedTotalPages =
        typeof response.totalPages === "number" && response.totalPages > 0
          ? response.totalPages
          : normalizedTotalResults > 0
          ? Math.ceil(normalizedTotalResults / ITEMS_PER_PAGE)
          : 1;

      setApplications(normalizedData);
      setTotalResults(normalizedTotalResults);
      setTotalPages(normalizedTotalPages);
      const normalizedJobOptions = Array.isArray(response.jobOptions)
        ? response.jobOptions
            .map((option) => ({
              jobId: option.jobId,
              title: option.title || option.jobId,
            }))
            .filter((option) => option.jobId)
        : [];
      const enrichedJobOptions =
        jobFilter !== "all" && !normalizedJobOptions.some((option) => option.jobId === jobFilter)
          ? [{ jobId: jobFilter, title: jobFilter }, ...normalizedJobOptions]
          : normalizedJobOptions;
      setJobOptionsList(enrichedJobOptions);

      if (normalizedTotalPages > 0 && currentPage > normalizedTotalPages) {
        setCurrentPage(normalizedTotalPages);
      }

      return normalizedData;
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load applications");
      setApplications([]);
      setJobOptionsList([]);
      setTotalResults(0);
      setTotalPages(1);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, dateFrom, dateTo, debouncedSearchTerm, jobFilter, statusFilter]);

  useEffect(() => {
    if (!dateError) {
      void fetchApplications();
    }
  }, [fetchApplications, dateError]);

  useEffect(() => {
    if (dateFrom && dateTo) {
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      if (from > to) {
        setDateError("End date cannot be before start date.");
        return;
      }
    }
    setDateError(null);
  }, [dateFrom, dateTo]);

  const jobOptions = useMemo(() => {
    const uniqueJobs = new Map<string, string>();
    applications.forEach((application) => {
      const jobId = application.jobInfo?.jobId;
      if (jobId) {
        const jobTitle = application.jobInfo?.title || "Untitled Job";
        uniqueJobs.set(jobId, `${jobTitle} (${jobId})`);
      }
    });
    return Array.from(uniqueJobs.entries()).map(([value, label]) => ({ value, label }));
  }, [applications]);

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }, [totalPages]);

  const openRejectDialog = (application: CampusDriveApplication) => {
    if (application.status?.toLowerCase() !== "applied") {
      return;
    }
    setPendingActionApplication(application);
    setRejectionReason("");
    setIsRejectDialogOpen(true);
  };

  const openShortlistDialog = (application: CampusDriveApplication) => {
    if (application.status?.toLowerCase() !== "applied") {
      return;
    }
    setPendingActionApplication(application);
    setIsShortlistDialogOpen(true);
  };

  const handleShortlist = async (application: CampusDriveApplication) => {
    if (application.status?.toLowerCase() !== "applied") {
      toast.error("Candidate status already updated");
      return;
    }
    setStatusUpdatingId(application._id);
    try {
      await updatePublicJobApplication(application._id, "shortlisted");
      toast.success("Candidate shortlisted");
      const updatedData = await fetchApplications();
      if (updatedData.length > 0) {
        const refreshed = updatedData.find((app) => app._id === application._id);
        if (refreshed) {
          setSelectedApplication((prev) => (prev && prev._id === refreshed._id ? refreshed : prev));
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to shortlist candidate");
    } finally {
      setStatusUpdatingId(null);
      setIsShortlistDialogOpen(false);
      setPendingActionApplication(null);
    }
  };

  const handleReject = async () => {
    if (!pendingActionApplication) return;
    if (!rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    const application = pendingActionApplication;
    if (application.status?.toLowerCase() !== "applied") {
      toast.error("Candidate status already updated");
      setIsRejectDialogOpen(false);
      setPendingActionApplication(null);
      setRejectionReason("");
      return;
    }

    setStatusUpdatingId(application._id);
    try {
      await updatePublicJobApplication(application._id, "rejected", rejectionReason.trim());
      toast.success("Candidate rejected");
      const updatedData = await fetchApplications();
      if (updatedData.length > 0) {
        const refreshed = updatedData.find((app) => app._id === application._id);
        if (refreshed) {
          setSelectedApplication((prev) => (prev && prev._id === refreshed._id ? refreshed : prev));
        }
      }
      setIsRejectDialogOpen(false);
      setPendingActionApplication(null);
      setRejectionReason("");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to reject candidate");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const getStatusStyles = (status: string) => {
    switch (status.toLowerCase()) {
      case "applied":
        return "bg-blue-100 text-blue-700";
      case "shortlisted":
        return "bg-green-100 text-green-700";
      case "rejected":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const hasVisibleResults = totalResults > 0 && applications.length > 0;
  const startEntry = hasVisibleResults
    ? Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalResults)
    : 0;
  const endEntry = hasVisibleResults
    ? Math.min((currentPage - 1) * ITEMS_PER_PAGE + applications.length, totalResults)
    : 0;

  return (
    <div className="bg-background h-full flex flex-col">
      <div className="sticky top-0 z-30 bg-background border-b border-border">
        <div className="px-3 sm:px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,0.3fr),minmax(0,1fr)] lg:items-end">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search by name, phone, email, job title or job ID"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 w-full"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* <div className="flex flex-col gap-1">
                <Label
                  htmlFor="campus-drive-status"
                  className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                >
                  Status
                </Label>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => {
                    setStatusFilter(value);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger id="campus-drive-status" className="h-10">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              {/* <div className="flex flex-col gap-1">
                <Label
                  htmlFor="campus-drive-job"
                  className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                >
                  Job
                </Label>
                <Select
                  value={jobFilter}
                  onValueChange={(value) => {
                    setJobFilter(value);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger id="campus-drive-job" className="h-10">
                    <SelectValue placeholder="All Jobs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Jobs</SelectItem>
                    {jobOptionsList.map((job) => (
                      <SelectItem key={job.jobId} value={job.jobId}>
                        {job.title || job.jobId}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}

              <div className="flex flex-col gap-1">
                <Label
                  htmlFor="campus-drive-from-date"
                  className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                >
                  From Date
                </Label>
                <Input
                  id="campus-drive-from-date"
                  type="date"
                  value={dateFrom}
                  onChange={(event) => {
                    setDateFrom(event.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-10"
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label
                  htmlFor="campus-drive-to-date"
                  className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                >
                  To Date
                </Label>
                <Input
                  id="campus-drive-to-date"
                  type="date"
                  value={dateTo}
                  onChange={(event) => {
                    setDateTo(event.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-10"
                />
              </div>
            </div>
          </div>
          {dateError ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {dateError}
            </div>
          ) : null}
          {error ? (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-[#FFF5F2]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Candidate
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Phone
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Job Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  led by
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Applied On
                </th>
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Status / Actions
                </th> */}
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider sticky top-0 bg-[#FFF5F2]">
                  Campus Eligible
                </th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin mb-3" />
                    Loading applications...
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-muted-foreground">
                    No campus drive applications found.
                  </td>
                </tr>
              ) : (
                applications.map((application) => {
                  let appliedOn = "—";
                  if (application.createdAt) {
                    const parsedDate = new Date(application.createdAt);
                    if (!Number.isNaN(parsedDate.getTime())) {
                      appliedOn = format(parsedDate, "dd MMM yyyy, hh:mm a");
                    }
                  }

                  const jobTitle = application.jobInfo?.title || "—";
                  const location = application.jobInfo?.location || "—";
                  const status = application.status || "Unknown";
                  const statusLower = status.toLowerCase();

                  return (
                    <tr
                      key={application._id}
                      className="hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedApplication(application);
                        setIsDetailsOpen(true);
                      }}
                    >
                      <td className="px-4 py-4 text-sm text-foreground font-medium">
                        {application.fullName}
                      </td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">
                        {application.phone || "—"}
                      </td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">
                        {application.email || "—"}
                      </td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">{jobTitle}</td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">{location}</td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">{application.ledByUser?.email || "—"}</td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">{appliedOn}</td>
                      {/* <td className="px-4 py-4">
                        {statusLower === "applied" ? (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-500 text-green-600 hover:bg-green-50"
                              onClick={(event) => {
                                event.stopPropagation();
                                openShortlistDialog(application);
                              }}
                              disabled={statusUpdatingId === application._id}
                            >
                              {statusUpdatingId === application._id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Shortlist"
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                              onClick={(event) => {
                                event.stopPropagation();
                                openRejectDialog(application);
                              }}
                              disabled={statusUpdatingId === application._id}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <Badge className={`${getStatusStyles(statusLower)} capitalize`}>{status}</Badge>
                        )}
                      </td> */}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-t border-border bg-background px-3 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-xs sm:text-sm text-muted-foreground">
          Showing{" "}
          <span className="font-medium text-foreground">
            {startEntry === 0 && endEntry === 0 ? "0" : `${startEntry}-${endEntry}`}
          </span>{" "}
          of{" "}
          <span className="font-medium text-foreground">{totalResults}</span>{" "}
          applications
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || isLoading}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>
          {pageNumbers.map((page) => (
            <Button
              key={page}
              variant={page === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => setCurrentPage(page)}
              disabled={isLoading || page === currentPage}
              className={page === currentPage ? "bg-[#EC6E51] text-white" : ""}
            >
              {page}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || isLoading}
            className="flex items-center gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog
        open={isDetailsOpen}
        onOpenChange={(open) => {
          setIsDetailsOpen(open);
          if (!open) {
            setSelectedApplication(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Candidate Details</DialogTitle>
            <DialogDescription>
              {selectedApplication?.fullName || "Candidate information"}
            </DialogDescription>
          </DialogHeader>

          {selectedApplication ? (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DetailField label="Full Name" value={selectedApplication.fullName} />
                <DetailField label="Email" value={selectedApplication.email} />
                <DetailField label="Phone" value={selectedApplication.phone} />
                <DetailField
                  label="Applied On"
                  value={
                    selectedApplication.createdAt
                      ? format(new Date(selectedApplication.createdAt), "dd MMM yyyy, hh:mm a")
                      : "—"
                  }
                />
                <DetailField
                  label="Job Title"
                  value={selectedApplication.jobInfo?.title}
                />
                <DetailField
                  label="Job Location"
                  value={selectedApplication.jobInfo?.location}
                />
                <DetailField label="Job ID" value={selectedApplication.jobInfo?.jobId} />
                <DetailField label="Source" value={selectedApplication.source} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DetailField
                  label="Highest Qualification"
                  value={selectedApplication.highestQualification}
                />
                <DetailField
                  label="Total Experience"
                  value={
                    selectedApplication.totalExperienceYears != null
                      ? `${selectedApplication.totalExperienceYears}y ${selectedApplication.totalExperienceMonths ?? 0}m`
                      : undefined
                  }
                />
                <DetailField
                  label="Spoken Languages"
                  value={
                    Array.isArray(selectedApplication.spokenLanguages)
                      ? selectedApplication.spokenLanguages.join(", ")
                      : undefined
                  }
                />
                <DetailField
                  label="Skills"
                  value={
                    Array.isArray(selectedApplication.skills)
                      ? selectedApplication.skills.join(", ")
                      : undefined
                  }
                />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={isShortlistDialogOpen}
        onOpenChange={(open) => {
          setIsShortlistDialogOpen(open);
          if (!open) {
            setPendingActionApplication(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Shortlist Candidate</DialogTitle>
            <DialogDescription>
              Are you sure you want to shortlist{" "}
              {pendingActionApplication?.fullName || "this candidate"}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsShortlistDialogOpen(false);
                setPendingActionApplication(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (pendingActionApplication) {
                  void handleShortlist(pendingActionApplication);
                }
              }}
              disabled={
                pendingActionApplication
                  ? statusUpdatingId === pendingActionApplication._id
                  : false
              }
            >
              {pendingActionApplication &&
              statusUpdatingId === pendingActionApplication._id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          setIsRejectDialogOpen(open);
          if (!open) {
            setPendingActionApplication(null);
            setRejectionReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting{" "}
              {pendingActionApplication?.fullName || "this candidate"}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="rejection-reason" className="text-xs uppercase tracking-wide">
              Rejection Reason
            </Label>
            <Textarea
              id="rejection-reason"
              placeholder="Enter the reason for rejection"
              value={rejectionReason}
              onChange={(event) => setRejectionReason(event.target.value)}
              rows={4}
            />
          </div>

          <DialogFooter className="pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsRejectDialogOpen(false);
                setPendingActionApplication(null);
                setRejectionReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleReject()}
              disabled={
                !rejectionReason.trim() ||
                (!!pendingActionApplication && statusUpdatingId === pendingActionApplication._id)
              }
            >
              {pendingActionApplication && statusUpdatingId === pendingActionApplication._id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailField: React.FC<{
  label: string;
  value?: string | number | null;
  badgeClass?: string | null;
}> = ({ label, value, badgeClass }) => {
  const displayValue =
    value === undefined || value === null || value === "" ? "—" : String(value);

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      {badgeClass ? (
        <Badge className={`${badgeClass} w-fit capitalize`}>{displayValue}</Badge>
      ) : (
        <span className="text-sm font-medium text-foreground break-words">{displayValue}</span>
      )}
    </div>
  );
};

export default CampusDriveApplications;

